import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  Cog6ToothIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import tenantService from '../../services/tenantService';
import { useToast } from '../../contexts/ToastContext';

const GlobalTenantSettings = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showError } = useToast();

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        setLoading(true);
        const data = await tenantService.getTenants();
        setTenants(data || []);
      } catch (error) {
        showError('Erreur lors du chargement des tenants');
      } finally {
        setLoading(false);
      }
    };

    fetchTenants();
  }, [showError]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Tenants</h1>
          <p className="mt-1 text-sm text-gray-600">Institutions financières configurées dans la plateforme.</p>
        </div>
        <Link to="/global/settings/new" className="btn-primary inline-flex items-center gap-2">
          <PlusIcon className="h-5 w-5" />
          Nouveau tenant
        </Link>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
          <BuildingOffice2Icon className="h-5 w-5 text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-900">Liste des institutions</h2>
        </div>

        {loading ? (
          <div className="px-5 py-10 text-sm text-gray-500">Chargement...</div>
        ) : tenants.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <BuildingOffice2Icon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-3 text-sm text-gray-600">Aucun tenant configuré.</p>
            <Link to="/global/settings/new" className="btn-primary mt-4 inline-flex items-center gap-2">
              <PlusIcon className="h-5 w-5" />
              Créer le premier tenant
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Institution</th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Devise</th>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Contact</th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {tenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-900">{tenant.name}</div>
                      <div className="text-sm text-gray-500">{tenant.description || 'Aucune description'}</div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">{tenant.institution_label || tenant.institution_type}</td>
                    <td className="px-5 py-4 text-sm text-gray-700">{tenant.settings?.general?.currency || 'HTG'}</td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      <div>{tenant.contact || '-'}</div>
                      <div className="text-gray-500">{tenant.email || tenant.phone || ''}</div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Link
                        to={`/global/settings/${tenant.id}`}
                        className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        <Cog6ToothIcon className="h-4 w-4" />
                        Configurer
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalTenantSettings;
